/**
 * The RBAC matrix: one row per permission, one column per role.
 *
 * `GET /api/admin/roles` is authoritative; `ROLE_PERMISSIONS` in lib/types.ts is
 * only a mirror so the grid can render without a round trip. Because the two are
 * supposed to be value-identical, this component also DIFFS them and warns when
 * they disagree — a drift there means the SPA is hiding or showing controls that
 * the server judges differently, which is exactly the bug class the frozen
 * permission vocabulary exists to prevent.
 *
 * The point of the whole panel is to make an auditor's reach obvious: three
 * `*.read` permissions, nothing that mutates.
 */

import { Trans, useLingui } from '@lingui/react/macro';
import { useMemo, type ReactNode } from 'react';

import { Banner } from '../../components/Banner';
import { Chip } from '../../components/Chip';
import { Icon } from '../../components/Icon';
import { uniqueStrings } from '../../lib/format';
import { intlLocale } from '../../lib/locale';
import {
  PERMISSIONS,
  PERMISSION_DESCRIPTIONS,
  ROLE_LABELS,
  ROLE_PERMISSIONS,
  isPermission,
  isRole,
} from '../../lib/types';
import type { Role, RoleInfo } from '../../lib/types';

/** Undefined for a permission the server sent but the SPA does not know. */
function permissionDescription(permission: string): string | undefined {
  const descriptions: Readonly<Record<string, string | undefined>> = PERMISSION_DESCRIPTIONS;
  return descriptions[permission];
}

function roleLabel(role: string): string {
  const labels: Readonly<Record<string, string | undefined>> = ROLE_LABELS;
  return labels[role] ?? role;
}

export interface RolesMatrixProps {
  /** Rows of `GET /api/admin/roles`, or the mirrored fallback. */
  roles: readonly RoleInfo[];
  /** True when `roles` is the client-side mirror because the request failed. */
  fallback: boolean;
  /** German detail of the failed request, shown verbatim. */
  fallbackReason: string | null;
  /** Roles of the signed-in account — their column gets a marker. */
  ownRoles: readonly Role[];
}

export function RolesMatrix({
  roles,
  fallback,
  fallbackReason,
  ownRoles,
}: RolesMatrixProps): ReactNode {
  const { t, i18n } = useLingui();
  const locale = i18n.locale;
  /** Known permissions in the frozen order, then anything new the server sent. */
  const permissionRows = useMemo<readonly string[]>(() => {
    const extra = uniqueStrings(roles.flatMap((role) => role.permissions))
      .filter((permission) => !isPermission(permission))
      .sort((a, b) => a.localeCompare(b, intlLocale()));
    return [...PERMISSIONS, ...extra];
    // `locale`: collation follows the UI language.
  }, [roles, locale]);

  /** Role name -> its permissions as plain strings (rows may be unknown values). */
  const grantedByRole = useMemo<ReadonlyMap<string, ReadonlySet<string>>>(
    () => new Map(roles.map((info) => [String(info.name), new Set<string>(info.permissions)])),
    [roles],
  );

  /** Differences between the server matrix and the SPA mirror. */
  const drift = useMemo<readonly string[]>(() => {
    if (fallback) return [];
    const out: string[] = [];
    const seen = new Set<string>();
    for (const info of roles) {
      const role = info.name;
      seen.add(role);
      if (!isRole(role)) {
        out.push(t`Rolle „${role}“ kennt der Server, die Oberfläche nicht.`);
        continue;
      }
      const mirrored = new Set<string>(ROLE_PERMISSIONS[role]);
      const server = new Set<string>(info.permissions);
      for (const permission of info.permissions) {
        if (!mirrored.has(permission)) {
          out.push(t`${role}: Server gewährt „${permission}“, die Oberfläche nicht.`);
        }
      }
      for (const permission of mirrored) {
        if (!server.has(permission)) {
          out.push(t`${role}: Oberfläche gewährt „${permission}“, der Server nicht.`);
        }
      }
    }
    for (const role of Object.keys(ROLE_PERMISSIONS)) {
      if (!seen.has(role)) out.push(t`Rolle „${role}“ kennt die Oberfläche, der Server nicht.`);
    }
    return out;
  }, [roles, fallback, locale]);

  return (
    <section className="card">
      <div className="card-head table-head">
        <h2><Trans>Rollen &amp; Berechtigungen</Trans></h2>
      </div>
      {fallback || drift.length > 0 ? (
        <div className="card-body adm-matrix-banners">
          {fallback ? (
            <Banner kind="warn" sub={fallbackReason ?? undefined}>
              <Trans>
                Die Rollenmatrix konnte nicht vom Server gelesen werden. Angezeigt wird die mitgelieferte
                Fassung, die vom Server abweichen kann.
              </Trans>
            </Banner>
          ) : null}
          {drift.length > 0 ? (
            <Banner kind="error" items={drift}>
              <Trans>
                Server und Oberfläche beschreiben die Rollen unterschiedlich. Es gelten die Rechte des
                Servers; einzelne Schaltflächen können deshalb fälschlich aktiv oder gesperrt sein.
              </Trans>
            </Banner>
          ) : null}
        </div>
      ) : null}

      <div className="tablewrap">
        <table className="adm-matrix" style={{ minWidth: 640 }}>
          <thead>
            <tr>
              <th><Trans>Berechtigung</Trans></th>
              {roles.map((info) => (
                <th key={info.name} className="center">
                  {roleLabel(info.name)}
                  {ownRoles.includes(info.name) ? (
                    <div style={{ marginTop: 'var(--space-1)' }}>
                      <Chip tone="info"><Trans>Ihr Konto</Trans></Chip>
                    </div>
                  ) : null}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {permissionRows.map((permission) => (
              <tr key={permission}>
                <td className="perm">
                  {permission}
                  <div className="adm-perm-desc">
                    {permissionDescription(permission)
                      ?? t`Vom Server gemeldet, in der Oberfläche nicht beschrieben.`}
                  </div>
                </td>
                {roles.map((info) => {
                  const granted = grantedByRole.get(String(info.name))?.has(permission) ?? false;
                  return (
                    <td key={`${info.name}-${permission}`} className="center">
                      <span
                        className={granted ? 'yes' : 'no'}
                        role="img"
                        aria-label={granted ? t`erlaubt` : t`nicht erlaubt`}
                      >
                        {granted ? <Icon name="check" size={14} /> : '–'}
                      </span>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="table-note">
        <Trans>
          Auditoren sehen alle Daten, können aber nichts auslösen: keine Bewertung, keine
          Benachrichtigung, kein Scan, kein Import, kein Schema-Eingriff.
        </Trans>
      </p>
    </section>
  );
}

export default RolesMatrix;
